/**
 * The calendar's arithmetic (073): which days a view shows, where a block
 * goes, how overlapping blocks share a column, and what a working day holds.
 *
 * Pure, like lib/quickAdd.js, and for the same reason: every answer is worked
 * out from the days and times passed in, never from the clock, so the screen
 * and its tests agree about "this week" whatever day they run on.
 *
 * DAYS ARE LOCAL CALENDAR DAYS, 'YYYY-MM-DD'; TIMES ARE 'HH:MM' on the wall
 * clock in Malta - the to-do's own due_time, which has no zone (056). A
 * meeting is an instant, and is placed by the browser's own clock, which in
 * this firm is Malta's.
 */
import { addDays, daysBetween, isoDay, parseDay } from './quickAdd';

export const DEFAULT_BLOCK = 30;     // minutes, for a timed to-do with no duration
export const SNAP = 15;              // minutes a drag snaps to
export const DAY_START = 7 * 60;     // the grid runs 07:00 ...
export const DAY_END = 20 * 60;      // ... to 20:00, wider when something is outside it
export const WORKDAY_HOURS = 8;      // the planned bar's day, until somebody says otherwise

const pad = (n) => String(n).padStart(2, '0');

/** 'HH:MM' as minutes after midnight, or null. */
export const toMinutes = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** Minutes after midnight as 'HH:MM' (24:00 and over is held at 23:59). */
export const toClock = (min) => {
  const n = Math.max(0, Math.min(24 * 60 - 1, Math.round(min)));
  return `${pad(Math.floor(n / 60))}:${pad(n % 60)}`;
};

/** To the nearest `step` minutes. */
export const snap = (min, step = SNAP) => Math.round(min / step) * step;

/** The Monday on or before a day. */
export const mondayOf = (day) => {
  const d = parseDay(day);
  return isoDay(addDays(d, -((d.getDay() + 6) % 7)));
};

/**
 * The days a view shows, in order. week: Monday to Sunday around `anchor`;
 * days3: `anchor` and the two after it; day: `anchor`; month: whole weeks
 * from the Monday on or before the 1st to the Sunday on or after the last.
 */
export function daysFor(view, anchor) {
  if (view === 'day') return [anchor];
  if (view === 'days3') return [0, 1, 2].map((i) => isoDay(addDays(parseDay(anchor), i)));
  if (view === 'month') {
    const a = parseDay(anchor);
    const first = isoDay(new Date(a.getFullYear(), a.getMonth(), 1));
    const last = isoDay(new Date(a.getFullYear(), a.getMonth() + 1, 0));
    const start = mondayOf(first);
    const end = isoDay(addDays(parseDay(mondayOf(last)), 6));
    const out = [];
    for (let d = start; d <= end; d = isoDay(addDays(parseDay(d), 1))) out.push(d);
    return out;
  }
  const mon = mondayOf(anchor);
  return [0, 1, 2, 3, 4, 5, 6].map((i) => isoDay(addDays(parseDay(mon), i)));
}

/** Where ‹ and › go from `anchor` in a view. */
export function step(view, anchor, dir) {
  const a = parseDay(anchor);
  if (view === 'month') return isoDay(new Date(a.getFullYear(), a.getMonth() + dir, 1));
  if (view === 'day') return isoDay(addDays(a, dir));
  if (view === 'days3') return isoDay(addDays(a, 3 * dir));
  if (view === 'agenda') return isoDay(addDays(a, 7 * dir));
  return isoDay(addDays(a, 7 * dir));
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];

/** The heading for what is shown: "19 – 25 Oct 2026", "October 2026", "Thu 22 Oct". */
export function rangeTitle(view, days) {
  if (!days.length) return '';
  const a = parseDay(days[0]);
  const b = parseDay(days[days.length - 1]);
  if (view === 'month') {
    const mid = parseDay(days[Math.floor(days.length / 2)]);
    return `${MONTH_LONG[mid.getMonth()]} ${mid.getFullYear()}`;
  }
  if (days.length === 1) return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][a.getDay()]} ${a.getDate()} ${MON[a.getMonth()]} ${a.getFullYear()}`;
  if (a.getMonth() === b.getMonth()) return `${a.getDate()} – ${b.getDate()} ${MON[b.getMonth()]} ${b.getFullYear()}`;
  if (a.getFullYear() === b.getFullYear()) return `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]} ${b.getFullYear()}`;
  return `${a.getDate()} ${MON[a.getMonth()]} ${a.getFullYear()} – ${b.getDate()} ${MON[b.getMonth()]} ${b.getFullYear()}`;
}

/** An instant as the local day and minute it falls on. */
export const localOf = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { day: isoDay(d), min: d.getHours() * 60 + d.getMinutes() };
};

/**
 * A meeting or an appointment cut to each day it touches: [{ day, start,
 * end }] in minutes - one entry, or more for one that runs past midnight.
 */
export function eventDays(startsAt, endsAt) {
  const s = localOf(startsAt);
  const e = localOf(endsAt);
  if (!s || !e) return [];
  const out = [];
  for (let d = s.day; d <= e.day; d = isoDay(addDays(parseDay(d), 1))) {
    const start = d === s.day ? s.min : 0;
    const end = d === e.day ? e.min : 24 * 60;
    if (end > start) out.push({ day: d, start, end });
    if (out.length > 62) break;
  }
  return out;
}

/** A to-do's block on its day: { start, end } in minutes, or null when it has no time. */
export function blockOf(item) {
  const start = toMinutes(item.due_time);
  if (start === null) return null;
  const len = Number(item.duration_minutes) > 0 ? Number(item.duration_minutes) : DEFAULT_BLOCK;
  return { start, end: Math.min(24 * 60, start + len) };
}

/**
 * Overlapping blocks side by side, as every calendar does: each block gets
 * `col` (0-based) and `cols` (how many share its cluster). Input [{ key,
 * start, end }]; the same objects come back with the two fields added, in
 * start order. A block shorter than 20 minutes is counted as 20 for
 * overlapping, since that is the least it is drawn as.
 */
export function layoutDay(blocks) {
  const sorted = blocks.slice().sort((a, b) => (a.start - b.start) || (b.end - a.end)
    || String(a.key).localeCompare(String(b.key)));
  const out = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const cols = [];
    for (const b of cluster) {
      let c = cols.findIndex((end) => end <= b.start);
      if (c < 0) { c = cols.length; cols.push(0); }
      cols[c] = Math.max(b.end, b.start + 20);
      b.col = c;
    }
    for (const b of cluster) b.cols = cols.length;
    out.push(...cluster);
    cluster = [];
    clusterEnd = -1;
  };
  for (const raw of sorted) {
    const b = { ...raw };
    if (cluster.length && b.start >= clusterEnd) flush();
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, Math.max(b.end, b.start + 20));
  }
  if (cluster.length) flush();
  return out;
}

/**
 * The hours a grid shows: 07:00-20:00, widened to the hour around anything
 * timed outside them. `spans` are [{ start, end }] in minutes.
 */
export function gridHours(spans) {
  let from = DAY_START;
  let to = DAY_END;
  for (const s of spans) {
    if (s.start < from) from = Math.floor(s.start / 60) * 60;
    if (s.end > to) to = Math.min(24 * 60, Math.ceil(s.end / 60) * 60);
  }
  return { from, to };
}

/**
 * What a day holds, for the planned bar: the minutes of the to-dos that
 * have a duration, how many have none, and the working day it is measured
 * against. `items` are that day's open to-dos.
 */
export function planOf(items, workdayHours = WORKDAY_HOURS) {
  let minutes = 0;
  let unsized = 0;
  for (const t of items) {
    const n = Number(t.duration_minutes);
    if (n > 0) minutes += n; else unsized += 1;
  }
  return { minutes, unsized, capacity: Math.round(Number(workdayHours) * 60) || WORKDAY_HOURS * 60 };
}

/** How long, briefly: "6 h 30", "45 min" (the same words as quick add's chip). */
export const hoursLabel = (minutes) => {
  const n = Math.round(Number(minutes) || 0);
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h} h ${pad(m)}` : `${h} h`;
};

/** Days from `a` to `b`, re-exported for the view. */
export { daysBetween };

/*
 * 077: PUBLIC EVENT CALENDARS as layers - the MCC, Events in Gozo, Malta's
 * public holidays - as GET /todos/calendar sends them: `layers` (each with
 * `on`) and `feed_events` (of the layers that are on). Read-only, drawn in a
 * style of their own beside the to-dos and meetings.
 */

const MIN_FEED_BLOCK = 30;   // a feed event with no length is drawn as half an hour

/**
 * The feed events as the calendar places them: [{ key, id, feed_id, layer,
 * color, title, location, url, description, starts_at, ends_at, allDay,
 * days }]. All-day ones - and timed ones a day long or more, an exhibition
 * "from the 10th to the 20th", which as a block would fill every column -
 * carry `days`, every local day they cover; the rest are timed. Events of a
 * layer that is not (or no longer) on are left out.
 */
export function feedItems(events, layers) {
  const byId = new Map((layers || []).filter((l) => l && l.on).map((l) => [l.id, l]));
  const out = [];
  for (const e of events || []) {
    const layer = byId.get(e.feed_id);
    if (!layer || !e || !e.starts_at) continue;
    const base = {
      key: `f:${e.id}`, id: e.id, feed_id: e.feed_id, layer, color: layer.color || null, title: e.title || 'Event',
      location: e.location || null, url: e.url || null, description: e.description || null,
      starts_at: e.starts_at, ends_at: e.ends_at || e.starts_at
    };
    const s = new Date(base.starts_at);
    const en = new Date(base.ends_at);
    if (Number.isNaN(s.getTime())) continue;
    const long = !Number.isNaN(en.getTime()) && en - s >= 24 * 3600 * 1000;
    if (e.all_day || long) {
      const first = e.all_day && e.day ? e.day : isoDay(s);
      let last = e.all_day && e.last_day ? e.last_day : isoDay(new Date(en.getTime() - 1));
      if (last < first) last = first;
      const days = [];
      for (let d = first; d <= last && days.length < 62; d = isoDay(addDays(parseDay(d), 1))) days.push(d);
      out.push({ ...base, allDay: true, timedLong: !e.all_day, days });
    } else {
      const end = Number.isNaN(en.getTime()) || en <= s ? new Date(s.getTime() + MIN_FEED_BLOCK * 60000) : en;
      out.push({ ...base, allDay: false, ends_at: end.toISOString(), real_end: base.ends_at, days: [] });
    }
  }
  return out;
}

const SHORT_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SHORT_WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayWords = (d) => { const x = parseDay(d); return `${SHORT_WD[x.getDay()]} ${x.getDate()} ${SHORT_MON[x.getMonth()]}`; };

/** When a feed event is, in words: "Fri 30 Oct, 20:30–22:30", "Sun 25 Oct, all day", "6 – 8 Nov, all day". */
export function feedWhen(item) {
  if (!item) return '';
  if (item.allDay) {
    const a = item.days[0];
    const b = item.days[item.days.length - 1];
    if (!item.timedLong) {
      if (a === b) return `${dayWords(a)}, all day`;
      const x = parseDay(a);
      const y = parseDay(b);
      return x.getMonth() === y.getMonth()
        ? `${x.getDate()} – ${y.getDate()} ${SHORT_MON[y.getMonth()]}, all day`
        : `${x.getDate()} ${SHORT_MON[x.getMonth()]} – ${y.getDate()} ${SHORT_MON[y.getMonth()]}, all day`;
    }
    const s = localOf(item.starts_at);
    const e = localOf(item.ends_at);
    return `${dayWords(s.day)} ${toClock(s.min)} – ${dayWords(e.day)} ${toClock(e.min)}`;
  }
  const s = localOf(item.starts_at);
  const e = localOf(item.real_end || item.ends_at);
  if (!s) return '';
  if (!e || item.real_end === item.starts_at) return `${dayWords(s.day)}, ${toClock(s.min)}`;
  return e.day === s.day ? `${dayWords(s.day)}, ${toClock(s.min)}–${toClock(e.min)}`
    : `${dayWords(s.day)} ${toClock(s.min)} – ${dayWords(e.day)} ${toClock(e.min)}`;
}

/**
 * A to-do from a feed event, for "Add to my to-dos": its day and time (no
 * time when it is all-day), how long it takes when that is known and under
 * a day, and the link and place in the notes.
 */
export function todoFromFeed(item) {
  const s = localOf(item.starts_at);
  const lines = [`${item.layer?.name || 'Calendar'} event${item.location ? ` · ${item.location}` : ''}`];
  if (item.url) lines.push(item.url);
  const draft = {
    title: String(item.title || 'Event').slice(0, 200),
    due_date: item.allDay ? item.days[0] : s.day,
    due_time: item.allDay ? null : toClock(s.min),
    duration_minutes: null,
    notes: lines.join('\n')
  };
  if (!item.allDay && item.real_end && item.real_end !== item.starts_at) {
    const mins = Math.round((new Date(item.real_end) - new Date(item.starts_at)) / 60000);
    if (mins >= 5 && mins <= 24 * 60) draft.duration_minutes = mins;
  }
  return draft;
}
